import { useMemo } from 'react';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import {
	selectAllEntryProperties,
	selectAllPropertyDefinitions,
	selectAllRepositories,
	selectAllRepositoryEntries,
	selectIsEntriesHydrated,
} from '../containers/contentSlice';
import { useGetRepositoryByEntryIdQuery, useLoadRepositoryEntry3Query } from '../services/repositoryEntries';
import { useGetSharedCollectionEntryQuery } from '../services/sharedCollections';
import { populateRepositoryEntriesForTable, repositoryEntriesApiRowToPopulatedEntry, sortEntryByRank } from '../common/util';
import { PopulatedEntry, PropertyDefinition, Repository } from '../types';
import useAuth from '../components/Auth';
import { useSharedView } from './useSharedView';
import { isNotFoundQueryError } from '../common/queryErrors';

export type UseRepositoryEntryDocumentDataResult = {
	populatedEntry: PopulatedEntry | null;
	repository: Repository | null;
	propertyDefinitions: PropertyDefinition[];
	isLoading: boolean;
	isNotFound: boolean;
};

/**
 * Resolves repository metadata, property definitions, and a populated entry row for a given
 * repository + repository entry id. Used anywhere entry content is shown (full page, drawer, etc.).
 */
export function useRepositoryEntryData(
	enabled: boolean,
	repositoryEntryId: string | null,
): UseRepositoryEntryDocumentDataResult {
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const { shareKey, isSharedView } = useSharedView();
	const { isAuthenticating } = useAuth();
	const isOwnerCloudData = isAuthenticated && !isSharedView;
	const isCloudData = isOwnerCloudData || isSharedView;
	const allRepositories = useAppSelector(selectAllRepositories);
	const allRepositoryEntries = useAppSelector(selectAllRepositoryEntries);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const allEntryProperties = useAppSelector(selectAllEntryProperties);
	const isEntriesHydrated = useAppSelector(selectIsEntriesHydrated);

	// `currentData` rather than `data`: `data` falls back to the last result this hook saw — kept even
	// across being skipped — whenever the cache entry is refetching, which would mount the (read-once)
	// entry form with values from before the latest save.
	const { currentData: ownerRepositoryResponse, isFetching: isRepoFetching, error: repositoryByEntryIdError } = useGetRepositoryByEntryIdQuery(repositoryEntryId as string, {
		skip: !enabled || !isOwnerCloudData || isAuthenticating || !repositoryEntryId,
	});
	const { currentData: ownerEntryBundle, isFetching: isEntryFetching, error: repositoryEntryBundleError } = useLoadRepositoryEntry3Query(repositoryEntryId as string, {
		skip: !enabled || !isOwnerCloudData || isAuthenticating || !repositoryEntryId,
	});
	const { currentData: sharedEntryResponse, isFetching: isSharedEntryFetching, error: sharedEntryError } = useGetSharedCollectionEntryQuery(
		{ shareKey: shareKey ?? '', entryId: repositoryEntryId ?? '' },
		{ skip: !enabled || !isSharedView || !repositoryEntryId },
	);
	const repositoryByEntryIdResponse = isSharedView ? sharedEntryResponse : ownerRepositoryResponse;
	const repositoryEntryBundle = isSharedView ? sharedEntryResponse?.entry : ownerEntryBundle;

	const localRepositoryId = useMemo(() => {
		if (!repositoryEntryId) {
			return null;
		}
		const localEntry = allRepositoryEntries.find((entry) => entry.id === repositoryEntryId);
		return localEntry?.repositoryId ?? null;
	}, [allRepositoryEntries, repositoryEntryId]);

	const repository = useMemo(() => {
		if (!enabled || !repositoryEntryId) {
			return null;
		}
		if (isCloudData) {
			return repositoryByEntryIdResponse?.repository ?? null;
		}
		return allRepositories.find((r) => r.id === localRepositoryId) ?? null;
	}, [repositoryByEntryIdResponse?.repository, isCloudData, allRepositories, localRepositoryId, repositoryEntryId, enabled]);

	const propertyDefinitions = useMemo(() => {
		if (!enabled || !repositoryEntryId) {
			return [];
		}
		const raw = isCloudData
			? repositoryByEntryIdResponse?.propertyDefinitions ?? []
			: allPropertyDefinitions.filter((def) => def.parentId === localRepositoryId);
		return [...raw].sort(sortEntryByRank) as PropertyDefinition[];
	}, [repositoryByEntryIdResponse?.propertyDefinitions, isCloudData, allPropertyDefinitions, localRepositoryId, repositoryEntryId, enabled]);

	const populatedEntry = useMemo((): PopulatedEntry | null => {
		if (!enabled || !repositoryEntryId) {
			return null;
		}
		if (isCloudData) {
			const row = repositoryEntryBundle?.rows?.[0];
			return row ? repositoryEntriesApiRowToPopulatedEntry(row) : null;
		}
		const repo = allRepositories.find((r) => r.id === localRepositoryId);
		if (!repo) {
			return null;
		}
		const row = populateRepositoryEntriesForTable(repo, allRepositoryEntries, allPropertyDefinitions, allEntryProperties).find(
			(entry) => entry.id === repositoryEntryId,
		);
		return row ? (repositoryEntriesApiRowToPopulatedEntry(row) as PopulatedEntry) : null;
	}, [
		enabled,
		repositoryEntryId,
		isCloudData,
		repositoryEntryBundle?.rows,
		localRepositoryId,
		allRepositories,
		allRepositoryEntries,
		allPropertyDefinitions,
		allEntryProperties,
	]);

	const isLoading = useMemo(() => {
		if (!enabled || !repositoryEntryId) {
			return false;
		}
		if (isSharedView) {
			return isSharedEntryFetching;
		}
		if (isCloudData) {
			return isRepoFetching || isEntryFetching;
		}
		return !repository || !populatedEntry;
	}, [enabled, repositoryEntryId, isSharedView, isSharedEntryFetching, isCloudData, isRepoFetching, isEntryFetching, repository, populatedEntry]);

	const isNotFound = enabled && Boolean(repositoryEntryId) && (isCloudData
		? [repositoryByEntryIdError, repositoryEntryBundleError, sharedEntryError].some(isNotFoundQueryError)
		: isEntriesHydrated && !localRepositoryId);

	return { populatedEntry, repository, propertyDefinitions, isLoading, isNotFound };
}
